import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '~/api/client';
import type { ApplicationDetail } from '~/api/types';
import { PermissionList } from '~/features/security/PermissionList';
import { SecurityPanel } from '~/features/security/SecurityPanel';
import {
  Badge,
  Breadcrumbs,
  Button,
  ErrorPanel,
  RiskBadge,
  Spinner,
  Stars,
  VerdictBadge,
  classes,
  formatCount,
  formatDate,
  formatRelative,
} from '~/shared/ui';
import { useQuery } from '~/shared/hooks/useQuery';
import { ReviewsPanel } from './ReviewsPanel';
import { NotFoundPage } from '~/shared/pages/NotFoundPage';

type Tab = 'overview' | 'permissions' | 'security' | 'releases' | 'reviews';

const TABS: readonly { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'permissions', label: 'Access' },
  { id: 'security', label: 'Security' },
  { id: 'releases', label: 'Releases' },
  { id: 'reviews', label: 'Reviews' },
];

/**
 * One listing, in full (§47).
 *
 * The tab lives in the URL so a link can point straight at the security report — which is the tab
 * people share when they want a second opinion before installing. Each tab's data is fetched only
 * when it is opened: the security report and the review list are separate, differently-cached
 * resources, and loading all of them for a visitor who only reads the description wastes both.
 */
export function ApplicationDetailPage(): JSX.Element {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = readTab(params.get('tab'));

  const detail = useQuery((signal) => api.application(slug, signal), [slug]);

  if (detail.error?.isNotFound === true) return <NotFoundPage />;
  if (detail.error !== undefined) return <ErrorPanel error={detail.error} onRetry={detail.reload} />;
  if (detail.data === undefined) {
    return (
      <div className="grid place-items-center py-24 text-slate-500">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  const application = detail.data;

  return (
    <>
      <Breadcrumbs
        trail={[
          { label: 'Applications', to: '/applications' },
          {
            label: application.categoryName,
            to: `/applications?category=${encodeURIComponent(application.categorySlug)}`,
          },
          { label: application.name },
        ]}
      />

      <Header application={application} />

      <nav className="mb-6 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-800">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-current={tab === entry.id ? 'page' : undefined}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (entry.id === 'overview') next.delete('tab');
              else next.set('tab', entry.id);
              setParams(next, { replace: true });
            }}
            className={classes(
              '-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm transition-colors',
              tab === entry.id
                ? 'border-brand text-slate-100'
                : 'border-transparent text-slate-400 hover:text-slate-200',
            )}
          >
            {entry.label}
            {entry.id === 'reviews' && application.ratingCount > 0 ? (
              <span className="ml-1.5 text-xs text-slate-500">{application.ratingCount}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === 'overview' ? <OverviewTab application={application} /> : null}
      {tab === 'permissions' ? <AccessTab application={application} /> : null}
      {tab === 'security' ? <SecurityTab slug={slug} /> : null}
      {tab === 'releases' ? <ReleasesTab slug={slug} /> : null}
      {tab === 'reviews' ? <ReviewsPanel slug={slug} onSubmitted={detail.reload} /> : null}
    </>
  );
}

function Header({ application }: { application: ApplicationDetail }): JSX.Element {
  const suspended = application.status === 'SUSPENDED';
  const deprecated = application.status === 'DEPRECATED';

  return (
    <div className="mb-8">
      {suspended ? (
        <p role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
          This listing is suspended and cannot be installed. Existing installations keep running.
        </p>
      ) : null}
      {deprecated ? (
        <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          The developer has deprecated this application. It is no longer accepting new installations.
        </p>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex min-w-0 gap-4">
          {application.logoUrl === undefined ? (
            <span
              aria-hidden="true"
              className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-slate-800 text-xl font-semibold text-slate-500"
            >
              {application.name.slice(0, 2).toUpperCase()}
            </span>
          ) : (
            <img
              src={application.logoUrl}
              alt=""
              className="h-16 w-16 shrink-0 rounded-xl bg-slate-800 object-cover"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-50">
              {application.name}
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              by{' '}
              <Link
                to={`/developers/${application.developerSlug}`}
                className="text-slate-300 hover:underline"
              >
                {application.developerName}
              </Link>
              {application.developerVerified ? (
                <span title="Verified developer" className="ml-1 text-brand">
                  ✓
                </span>
              ) : null}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Stars average={application.ratingAverage} count={application.ratingCount} />
              <span className="text-sm text-slate-500">
                {formatCount(application.installCount)} installs
              </span>
              <RiskBadge risk={application.permissionRisk} />
              <VerdictBadge verdict={application.security?.verdict} />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-2">
          <Link to={`/applications/${application.slug}/install`}>
            <Button variant="primary" disabled={suspended || deprecated} className="w-full">
              Install
            </Button>
          </Link>
          <p className="text-center text-xs text-slate-500">
            {application.pricingLabel}
            {application.latestVersion === undefined ? '' : ` · v${application.latestVersion}`}
          </p>
        </div>
      </div>

      <p className="mt-6 max-w-3xl text-slate-300">{application.summary}</p>
    </div>
  );
}

function OverviewTab({ application }: { application: ApplicationDetail }): JSX.Element {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_18rem]">
      <div>
        {application.screenshots.length > 0 ? (
          <div className="mb-8 -mx-4 flex snap-x gap-4 overflow-x-auto overflow-y-hidden px-4 pb-2">
            {application.screenshots.map((shot) => (
              <figure key={shot.url} className="w-80 shrink-0 snap-start">
                <img
                  src={shot.url}
                  alt={shot.caption ?? ''}
                  loading="lazy"
                  className="rounded-lg border border-slate-800"
                />
                {shot.caption === undefined ? null : (
                  <figcaption className="mt-1.5 text-xs text-slate-500">{shot.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        ) : null}

        {/*
         * Rendered as pre-wrapped text rather than parsed as markdown. The description comes from an
         * untrusted developer, and shipping a markdown renderer to turn it into HTML would open an
         * injection surface for the sake of formatting (§30).
         */}
        <div className="whitespace-pre-wrap text-slate-300">
          {application.description === '' ? application.summary : application.description}
        </div>

        {application.changelog === '' ? null : (
          <section className="mt-8">
            <h2 className="mb-2 font-semibold text-slate-100">
              What&rsquo;s new in {application.latestVersion ?? 'the latest release'}
            </h2>
            <div className="whitespace-pre-wrap text-sm text-slate-400">{application.changelog}</div>
          </section>
        )}
      </div>

      <aside className="space-y-4">
        <div className="card">
          <h2 className="mb-3 font-semibold text-slate-100">Details</h2>
          <dl className="space-y-2 text-sm">
            <Row label="Type" value={application.type.replace(/_/g, ' ').toLowerCase()} />
            <Row label="Category" value={application.categoryName} />
            <Row label="Version" value={application.latestVersion ?? '—'} />
            <Row label="Updated" value={formatRelative(application.updatedAt)} />
            <Row label="Published" value={formatDate(application.publishedAt)} />
            <Row label="Dependencies" value={String(application.dependencyCount)} />
          </dl>
          <details className="mt-4 border-t border-slate-800 pt-3">
            <summary className="cursor-pointer text-sm text-slate-400 hover:text-slate-200">
              Advanced details
            </summary>
            <dl className="mt-3 space-y-2 text-sm">
              {application.compatibilityDate === undefined ? null : (
                <Row label="Workers compatibility" value={application.compatibilityDate} />
              )}
              <Row
                label="Triggers"
                value={
                  application.triggers.length === 0
                    ? 'HTTP only'
                    : application.triggers
                        .map((trigger) => `${trigger.kind.toLowerCase()} ${trigger.value}`)
                        .join(', ')
                }
              />
              <Row
                label="Bindings"
                value={
                  application.resources.length === 0
                    ? 'None'
                    : String(application.resources.length)
                }
              />
            </dl>
          </details>
        </div>

        {application.tags.length > 0 ? (
          <div className="card">
            <h2 className="mb-3 font-semibold text-slate-100">Tags</h2>
            <div className="flex flex-wrap gap-1.5">
              {application.tags.map((tag) => (
                <Link key={tag} to={`/applications?tag=${encodeURIComponent(tag)}`}>
                  <Badge>{tag}</Badge>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        <div className="card">
          <h2 className="mb-3 font-semibold text-slate-100">Links</h2>
          <ul className="space-y-1.5 text-sm">
            <ExternalLink label="Homepage" url={application.homepageUrl} />
            <ExternalLink label="Source code" url={application.repositoryUrl} />
            <ExternalLink label="Support" url={application.supportUrl} />
          </ul>
        </div>
      </aside>
    </div>
  );
}

function AccessTab({ application }: { application: ApplicationDetail }): JSX.Element {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_20rem]">
      <div>
        <h2 className="mb-1 font-semibold text-slate-100">What this application can access</h2>
        <p className="mb-5 text-sm text-slate-400">
          These are derived from the source code as well as from what the developer declared. Anything
          found in the code but left undeclared is marked.
        </p>
        <PermissionList permissions={application.permissions} />
      </div>

      <aside className="space-y-4">
        <div className="card">
          <h3 className="mb-3 font-semibold text-slate-100">Created in your account</h3>
          {application.resources.length === 0 ? (
            <p className="text-sm text-slate-400">Nothing new is created.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {application.resources.map((resource) => (
                <li key={resource.bindingName} className="flex items-start justify-between gap-2">
                  <span className="text-slate-300">{resource.label}</span>
                  <span className="shrink-0 text-xs text-slate-500">
                    {resource.willBeCreated ? 'new' : 'you choose'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h3 className="mb-3 font-semibold text-slate-100">You will need to provide</h3>
          {application.secrets.length === 0 ? (
            <p className="text-sm text-slate-400">No credentials are needed.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {application.secrets.map((secret) => (
                <li key={secret.name}>
                  <p className="font-mono text-xs text-slate-300">{secret.name}</p>
                  {secret.description === undefined ? null : (
                    <p className="text-xs text-slate-500">{secret.description}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h3 className="mb-3 font-semibold text-slate-100">Talks to</h3>
          {application.externalServices.length === 0 ? (
            <p className="text-sm text-slate-400">
              No outbound connections outside Cloudflare were found.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {application.externalServices.map((service) => (
                <li key={service.host}>
                  <p className="flex items-center gap-1.5 font-mono text-xs text-slate-300">
                    {service.host}
                    {service.declared ? null : (
                      <Badge tone="warn" title="Found in the source but not declared">
                        undeclared
                      </Badge>
                    )}
                  </p>
                  {service.purpose === undefined ? null : (
                    <p className="text-xs text-slate-500">{service.purpose}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {application.triggers.length === 0 ? null : (
          <div className="card">
            <h3 className="mb-3 font-semibold text-slate-100">Runs on</h3>
            <ul className="space-y-1.5 text-sm text-slate-300">
              {application.triggers.map((trigger) => (
                <li key={`${trigger.kind}:${trigger.value}`}>
                  <span className="text-slate-500">{trigger.kind.replace(/_/g, ' ').toLowerCase()}: </span>
                  <span className="font-mono text-xs">{trigger.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function SecurityTab({ slug }: { slug: string }): JSX.Element {
  const report = useQuery((signal) => api.securityReport(slug, undefined, signal), [slug]);
  return (
    <SecurityPanel
      report={report.data}
      error={report.error}
      isLoading={report.isLoading}
      onRetry={report.reload}
    />
  );
}

function ReleasesTab({ slug }: { slug: string }): JSX.Element {
  const versions = useQuery((signal) => api.applicationVersions(slug, {}, signal), [slug]);
  const [expanded, setExpanded] = useState<string | undefined>(undefined);

  if (versions.error !== undefined) {
    return <ErrorPanel error={versions.error} onRetry={versions.reload} />;
  }
  if (versions.data === undefined) {
    return (
      <div className="grid place-items-center py-12 text-slate-500">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {versions.data.items.map((version) => (
        <li key={version.id} className="card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-semibold text-slate-100">v{version.version}</span>
              <VerdictBadge verdict={version.securityVerdict} />
              <span className="font-mono text-xs text-slate-500">{version.commitShortSha}</span>
            </div>
            <span className="text-sm text-slate-500">{formatDate(version.publishedAt)}</span>
          </div>
          {version.changelog === '' ? null : (
            <>
              <button
                type="button"
                aria-expanded={expanded === version.id}
                onClick={() => setExpanded(expanded === version.id ? undefined : version.id)}
                className="mt-3 text-sm text-brand-soft hover:underline"
              >
                {expanded === version.id ? 'Hide' : 'Show'} changelog
              </button>
              {expanded === version.id ? (
                <div className="mt-2 whitespace-pre-wrap text-sm text-slate-400">
                  {version.changelog}
                </div>
              ) : null}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-300">{value}</dd>
    </div>
  );
}

function ExternalLink({ label, url }: { label: string; url: string | undefined }): JSX.Element {
  if (url === undefined) {
    return (
      <li className="flex justify-between gap-3">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-600">—</span>
      </li>
    );
  }
  return (
    <li className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      {/*
       * `noopener` is not optional on a developer-supplied link: without it the target page can
       * reach back through `window.opener` and navigate this tab.
       */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="truncate text-brand-soft hover:underline"
      >
        {new URL(url).hostname}
      </a>
    </li>
  );
}

function readTab(value: string | null): Tab {
  return TABS.find((entry) => entry.id === value)?.id ?? 'overview';
}
